// Barber login creation (BARBER ONBOARDING 2026-08-02).
// Creates a Firebase Auth account for a NEW barber WITHOUT logging out the current admin
// (uses a throwaway SECONDARY app instance), links a users/{uid} record + role the rules
// read, and emails them a set-password link. Returns the uid to store on the barber record.
import { initializeApp, deleteApp } from 'firebase/app';
import { getAuth, createUserWithEmailAndPassword, sendPasswordResetEmail, signOut } from 'firebase/auth';
import { doc, setDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db, app } from '../config/firebase';
import { startOperatorSession } from './operatorSession';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

// Barber default permissions (own numbers + take/charge payments; no shop-wide access).
const BARBER_PERMS: Record<string, boolean> = {
  editSettings: false, manageStaff: false, assignRoles: false, viewReports: false, viewPayroll: false,
  manageClients: false, processPayments: true, chargePayments: true, manageInventory: false,
  canDiscount: false, canEmergencyBook: false,
};

export async function createBarberLogin(opts: {
  email: string; name: string; role?: string; barberId: string; permissions?: Record<string, boolean>;
}): Promise<{ uid: string; emailSent: boolean }> {
  const { email, name, role = 'barber', barberId, permissions } = opts;
  const secondary = initializeApp(firebaseConfig, `barber-signup-${Date.now()}`);
  try {
    const secAuth = getAuth(secondary);
    const tempPassword = Math.random().toString(36).slice(2) + 'Aa1!';
    const cred = await createUserWithEmailAndPassword(secAuth, email, tempPassword);
    const uid = cred.user.uid;
    // The users/{uid} record the Firestore rules gate on (role + flat permissions + barberId link).
    await setDoc(doc(db, 'users', uid), {
      email, name, role, barberId,
      permissions: permissions || BARBER_PERMS,
      isBarber: true, createdAt: new Date(), updatedAt: new Date(),
    }, { merge: true });
    // H31/M37 FIX (Jose 2026-10-29 audit): the set-password email used to fail SILENTLY (`catch {}`),
    // so the account+record were created, the UI showed "invited", but the barber never got the link and
    // was locked out with no trace. Now we LOG the failure and REPORT it via emailSent so the caller can
    // tell the admin "account created but the email didn't send — resend it" instead of faking success.
    let emailSent = false;
    try { await sendPasswordResetEmail(secAuth, email); emailSent = true; }
    catch (err) { console.error('[barberAuth] createBarberLogin: set-password email failed to send:', err); }
    await signOut(secAuth).catch(() => {});
    return { uid, emailSent };
  } finally {
    await deleteApp(secondary).catch(() => {});
  }
}

/**
 * Send (or resend) a barber's "set your password" invitation email.
 * - No login yet  -> create the account (createBarberLogin also emails the link) => 'sent'.
 * - Already has a login (or email already registered) -> just re-send the link => 'resent'.
 * Uses the MAIN auth instance for resend (does not disturb the current admin session).
 */
export async function inviteBarber(opts: {
  email: string; name: string; role?: string; barberId: string; hasLogin: boolean;
  permissions?: Record<string, boolean>;
}): Promise<{ status: 'sent' | 'resent'; uid?: string; emailSent?: boolean }> {
  const { email, hasLogin } = opts;
  if (hasLogin) {
    await sendPasswordResetEmail(getAuth(), email);
    return { status: 'resent', emailSent: true };
  }
  try {
    // H31/M37: surface emailSent so the caller knows whether the invite link actually went out.
    const { uid, emailSent } = await createBarberLogin(opts);
    return { status: 'sent', uid, emailSent };
  } catch (e: any) {
    if (e?.code === 'auth/email-already-in-use') {
      await sendPasswordResetEmail(getAuth(), email);
      return { status: 'resent', emailSent: true };
    }
    throw e;
  }
}

// ─── SERVER-SIDE PIN AUTH (barbers-PIN security refactor, Jose 2026-09-29, Plan A) ───────────────
// BEHAVIOR-PIN: replaces the old client-side `barbers.find(b => b.pin === entered)` — which required
// the raw PIN to sit on the world-readable `barbers` doc. PINs are now verified server-side against
// HASHED secrets in the Admin-only `barberSecrets` collection (functions/src/barberAuth.ts, pbkdf2 +
// rate-limit). We keep the shared shop-account UX: use the returned {barberId,name,role} to set the
// LOCAL barber identity and IGNORE the custom token (per-barber Firebase identity = future Phase B,
// task #14). change-watch: functions/src/barberAuth.ts

const fns = getFunctions(app); // default region us-central1 (matches deployed functions)

export interface BarberSignInResult {
  ok: boolean;
  barberId: string;
  name: string;
  role: string;
  token?: string; // available for a future per-barber-identity phase; not used today
}

export type BarberPinErrorCode = 'not-recognized' | 'rate-limited' | 'not-configured' | 'unknown';

export class BarberPinAuthError extends Error {
  code: BarberPinErrorCode;
  constructor(code: BarberPinErrorCode, message: string) {
    super(message);
    this.name = 'BarberPinAuthError';
    this.code = code;
  }
}

/** Verify a barber PIN server-side. Resolves with {barberId,name,role}; throws BarberPinAuthError on failure. */
export async function verifyBarberPin(pin: string, staySignedIn?: boolean): Promise<BarberSignInResult> {
  try {
    const fn = httpsCallable(fns, 'barberSignIn', { timeout: 20_000 });
    // wantToken:true → a per-operator custom token for the ephemeral operator session (Phase B).
    // staySignedIn → persisted server-side so the session can be RE-ESTABLISHED on reload without a PIN.
    const res: any = await fn({ pin, wantToken: true, staySignedIn: !!staySignedIn });
    const d = res?.data || {};
    const result = { ok: !!d.ok, barberId: d.barberId, name: d.name, role: d.role, token: d.token };
    // Start the operator's ephemeral session for SCOPED pay/financial reads. Fire-and-forget so PIN
    // sign-in stays fast; the pay subscriptions re-run when the session becomes ready (onOperatorChange).
    // M36 FIX (Jose 2026-10-29 audit): do NOT swallow a start failure silently — if the ephemeral
    // session can't establish (bad custom token, app init blocked), pay/earnings read EMPTY with zero
    // trace and the barber thinks they weren't paid. Log it loudly so it's diagnosable.
    if (result.ok && result.token && result.barberId) {
      startOperatorSession(result.token, result.barberId, result.role).catch((err) =>
        console.error('[barberAuth] operator session failed to start — pay/financial reads will be unavailable until re-tap:', err));
    }
    return result;
  } catch (e: any) {
    const code: string = e?.code || e?.message || '';
    if (code.includes('permission-denied')) throw new BarberPinAuthError('not-recognized', 'PIN not recognized. Try again.');
    if (code.includes('resource-exhausted')) throw new BarberPinAuthError('rate-limited', 'Too many attempts — wait a minute.');
    if (code.includes('failed-precondition') || code.includes('unauthenticated')) {
      throw new BarberPinAuthError('not-configured', 'This device isn’t signed in to the shop yet.');
    }
    throw new BarberPinAuthError('unknown', 'Something went wrong. Please try again.');
  }
}

/**
 * Re-establish a STAY-SIGNED-IN operator's ephemeral session on reload WITHOUT a PIN (Jose 2026-10-24).
 * The operator token is in-memory only, so after a reload the owner session is gone and owner-grade writes
 * (financial import, settings) silently fall back to the shared STAFF device account → permission denied.
 * The CF re-mints the token ONLY for a barber who explicitly opted into stay-signed-in. Best-effort.
 */
export async function restoreOperatorSession(barberId: string): Promise<boolean> {
  try {
    const fn = httpsCallable(fns, 'restoreOperatorSession', { timeout: 20_000 });
    const res: any = await fn({ barberId });
    const d = res?.data || {};
    if (d.ok && d.token && d.barberId) {
      await startOperatorSession(d.token, d.barberId, d.role);
      console.log('[operator] stay-signed-in session re-established on reload:', d.barberId);
      return true;
    }
  } catch (e: any) {
    console.warn('[operator] restore failed (owner-grade writes may need a PIN tap-in):', e?.code || e?.message || e);
  }
  return false;
}

/** Owner/manager: create or update a barber's PIN login (server-side hash → barberSecrets). */
export async function provisionBarberPin(
  barberId: string,
  pin: string
): Promise<{ ok: boolean; authUid?: string; role?: string }> {
  const fn = httpsCallable(fns, 'provisionBarberLogin', { timeout: 20_000 });
  const res: any = await fn({ barberId, pin });
  return res?.data || { ok: false };
}

/** Owner/manager: remove a barber's PIN login (deletes the hashed secret + clears the flag). */
export async function removeBarberPin(barberId: string): Promise<{ ok: boolean }> {
  const fn = httpsCallable(fns, 'removeBarberPin', { timeout: 20_000 });
  const res: any = await fn({ barberId });
  return res?.data || { ok: false };
}
